import { useQuery } from '@tanstack/react-query';
import { api, call } from '../../api/client';

/** Which ways to sign in this server offers (Google, Microsoft; email + password only outside production). */
export function useSignInOptions() {
  return useQuery({ queryKey: ['auth', 'providers'], queryFn: () => call(api.GET('/api/v1/auth/providers')), staleTime: 5 * 60_000 });
}

const ERRORS: Record<string, string> = {
  CANCELLED: 'Sign-in was cancelled.',
  EXPIRED: 'That sign-in took too long. Please try again.',
  FAILED: "We couldn't sign you in. Please try again.",
  EMAIL_BELONGS_TO_ACCOUNT:
    'That email already has a FamilyChores account. Sign in the way you did before, then connect this account under Your account → Sign-in methods.',
  IDENTITY_IN_USE: 'That account is already connected to a different FamilyChores account.',
  ACCOUNT_DISABLED: 'This account is disabled.',
};

export const signInErrorText = (code: string | undefined) => (code ? (ERRORS[code] ?? ERRORS['FAILED']) : undefined);

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.2l7.9 6.2C12.5 13.6 17.8 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.3 5.7c4.3-3.9 7-9.8 7-17.1z" />
      <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.2C1 16.6 0 20.2 0 24s1 7.4 2.7 10.8l7.9-6.2z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.3-5.7c-2 1.4-4.7 2.3-8.6 2.3-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.2C6.6 42.6 14.6 48 24 48z" />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden>
      <path fill="#f25022" d="M1 1h10v10H1z" />
      <path fill="#7fba00" d="M12 1h10v10H12z" />
      <path fill="#00a4ef" d="M1 12h10v10H1z" />
      <path fill="#ffb900" d="M12 12h10v10H12z" />
    </svg>
  );
}

const buttonClass =
  'flex w-full items-center justify-center gap-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-4 py-2.5 text-sm font-medium hover:bg-[var(--surface-2)]';

/** "Continue with Google / Microsoft": full-page navigations to the API, which hands off to the provider. */
export function ProviderButtons({ returnTo }: { returnTo?: string }) {
  const options = useSignInOptions().data;
  if (!options) return null;
  const href = (provider: string) =>
    `/api/v1/auth/oauth/${provider}/start?client=WEB${returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`;
  return (
    <div className="space-y-2">
      {options.google ? (
        <a className={buttonClass} href={href('google')}>
          <GoogleMark /> Continue with Google
        </a>
      ) : null}
      {options.microsoft ? (
        <a className={buttonClass} href={href('microsoft')}>
          <MicrosoftMark /> Continue with Microsoft
        </a>
      ) : null}
    </div>
  );
}
